import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../contexts/AuthContext';
import ModalBase from '../ModalBase';
import { exportToExcel } from '../../helpers/exportCsv';
import {
  GRAV, ORIGEM, ORDEM_ORIGENS, Carregando, Gravidade, PillOrigem, dataHora, dec, dia, duracao, horasCpu,
  media, num, pct, porExecucao, useOrdenacao, vezesOBanco,
} from './comum';

// ── Diagnostico de uma consulta (lido do plano na hora do clique) ───────────
export function Diagnostico({ id }) {
  const [d, setD] = useState(null);
  const [erro, setErro] = useState('');
  useEffect(() => {
    let valido = true;
    setD(null); setErro('');
    axios.get(`${API}/analise-banco/consultas/${id}/diagnostico`)
      .then(({ data }) => { if (valido) setD(data.data); })
      .catch((e) => { if (valido) setErro(e.response?.data?.message || 'Não foi possível ler o plano desta consulta.'); });
    return () => { valido = false; };
  }, [id]);
  if (erro) return <div className="feedback-banner feedback-banner--error">{erro}</div>;
  if (!d) {
    return (
      <div className="ab-diag ab-diag--carregando">
        <div className="screen-loader__spinner" />
        <span>Lendo o plano de execução no servidor para montar o diagnóstico (alguns segundos)…</span>
      </div>
    );
  }
  const quem = [...new Set(d.itens.filter((i) => i.gravidade !== 'baixa').map((i) => i.quem))];
  return (
    <section className="ab-diag">
      <div className="ab-diag__topo">
        <h4><i className="bi bi-clipboard2-pulse" /> O que resolveria</h4>
        <span className="cell-muted">
          {d.planoDisponivel ? `lido do plano de execução (${d.fontePlano === 'query_store' ? 'Query Store' : 'cache do servidor'})` : 'sem plano disponível'}
        </span>
      </div>
      <p className="ab-diag__resumo">{d.resumo}</p>
      {quem.length > 0 && <p className="ab-diag__quem"><span className="cell-muted">Quem resolve:</span> <strong>{quem.join(' · ')}</strong></p>}
      {d.itens.map((i) => (
        <article key={i.tipo + i.titulo} className="ab-diag__item" style={{ '--cor': (GRAV[i.gravidade] || GRAV.baixa).cor }}>
          <div className="ab-diag__titulo"><Gravidade g={i.gravidade} /><strong>{i.titulo}</strong></div>
          <dl>
            <div><dt>Por que pesa</dt><dd>{i.porque}</dd></div>
            <div><dt>O que resolveria</dt><dd>{i.sugestao}</dd></div>
            <div><dt>Quem resolve</dt><dd className="cell-strong">{i.quem}</dd></div>
          </dl>
          {i.evidencia?.length > 0 && (
            <details className="ab-diag__evidencia">
              <summary>Evidência no plano</summary>
              <ul>{i.evidencia.map((e) => <li key={e}><code>{e}</code></li>)}</ul>
            </details>
          )}
        </article>
      ))}
      {d.planos.length > 1 && (
        <details className="ab-diag__evidencia">
          <summary>Planos de execução deste comando ({d.planos.length})</summary>
          <div className="table-shell" style={{ marginTop: 6 }}>
            <table className="portal-table">
              <thead><tr><th>Plano</th><th style={{ textAlign: 'right' }}>Execuções</th><th style={{ textAlign: 'right' }}>Média</th><th style={{ textAlign: 'right' }}>Pior</th><th style={{ textAlign: 'right' }}>Leitura por execução</th><th>Criado em</th><th>Última execução</th></tr></thead>
              <tbody>
                {d.planos.map((p, i) => (
                  <tr key={i}>
                    <td>#{i + 1}</td>
                    <td style={{ textAlign: 'right' }}>{num(p.execucoes)}</td>
                    <td style={{ textAlign: 'right' }}>{duracao(media(p.duracao_ms, p.execucoes))}</td>
                    <td style={{ textAlign: 'right' }}>{duracao(p.max_duracao_ms)}</td>
                    <td style={{ textAlign: 'right' }}>{porExecucao(p.leituras, p.execucoes)}</td>
                    <td className="cell-muted">{dataHora(p.criado_em)}</td>
                    <td className="cell-muted">{dataHora(p.ultima_em)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </section>
  );
}

// Barras simples de CPU (por dia / por hora) — so existe para os portais (Query
// Store) ate a coleta de 15 min.
function MiniBarras({ itens, rotulo, max }) {
  return (
    <div className="ab-barras">
      {itens.map((d) => (
        <div key={d.chave} className="ab-barras__col" title={`${d.rotulo}: ${dec(Number(d.cpu_ms) / 1000, 1)} s de CPU · ${num(d.execucoes)} execuções`}>
          <div style={{ height: `${(100 * Number(d.cpu_ms)) / Math.max(1, max)}%` }} />
        </div>
      ))}
      <span className="sr-only">{rotulo}</span>
    </div>
  );
}

export function ModalConsulta({ id, onClose, tamanhoBancoGb, cpuTotal }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  useEffect(() => {
    axios.get(`${API}/analise-banco/consultas/${id}`)
      .then(({ data }) => setDados(data.data))
      .catch((e) => setErro(e.response?.data?.message || 'Não foi possível abrir a consulta.'));
  }, [id]);
  const c = dados?.consulta;
  const vezes = c ? vezesOBanco(c.leituras, tamanhoBancoGb) : null;
  return (
    <ModalBase titulo={c ? (c.objeto || 'Consulta') : 'Consulta'} onClose={onClose} largura={1000}>
      {erro && <div className="feedback-banner feedback-banner--error">{erro}</div>}
      {!c && !erro && <Carregando />}
      {c && (
        <>
          <div className="ab-numeros">
            <PillOrigem origem={c.origem} />
            {c.execucoes != null && (
              <>
                <span><strong>{num(c.execucoes)}</strong> execuções</span>
                <span><strong>{horasCpu(c.cpu_ms)}</strong> de CPU{cpuTotal ? ` (${pct(c.cpu_ms, cpuTotal)} do servidor)` : ''}</span>
                <span>normal <strong>{duracao(media(c.duracao_ms, c.execucoes))}</strong></span>
                <span>pior <strong>{duracao(c.max_duracao_ms)}</strong></span>
                <span title="Leitura lógica: cada página lida na memória conta, inclusive a mesma relida"><strong>{porExecucao(c.leituras, c.execucoes)}</strong> lidos por execução</span>
                {c.banco === 'BDENTER' && vezes >= 1 && <span>no total, o banco do ERP inteiro <strong>{num(vezes)}×</strong></span>}
                <span className="cell-muted">desde {dia(c.no_cache_desde)} · banco {c.banco}</span>
              </>
            )}
          </div>

          <Diagnostico id={c.id} />

          {dados.porDia.length > 0 ? (
            <div className="row g-3 mt-1">
              <div className="col-12 col-lg-7">
                <h4 className="ab-subtitulo">CPU por dia</h4>
                <MiniBarras rotulo="CPU por dia" max={Math.max(...dados.porDia.map((d) => Number(d.cpu_ms)))}
                  itens={dados.porDia.map((d) => ({ ...d, chave: d.dia, rotulo: dia(d.dia) }))} />
                <div className="ab-eixo-x"><span>{dia(dados.porDia[0].dia)}</span><span>{dia(dados.porDia[dados.porDia.length - 1].dia)}</span></div>
              </div>
              <div className="col-12 col-lg-5">
                <h4 className="ab-subtitulo">Hora do dia em que pesa</h4>
                <MiniBarras rotulo="CPU por hora" max={Math.max(...dados.porHora.map((d) => Number(d.cpu_ms)), 1)}
                  itens={Array.from({ length: 24 }, (_, h) => ({ ...(dados.porHora.find((x) => x.hora === h) || { cpu_ms: 0, execucoes: 0 }), chave: h, rotulo: `${h}h` }))} />
                <div className="ab-eixo-x"><span>0h</span><span>12h</span><span>23h</span></div>
              </div>
            </div>
          ) : (
            <p className="cell-muted mt-2">O horário desta consulta ainda não existe: para o ERP ele começa com a coleta a cada 15 minutos.</p>
          )}

          <details className="ab-recolhido">
            <summary>Ver o comando completo</summary>
            <pre className="ab-sql">{c.texto || '(texto indisponível)'}</pre>
          </details>
        </>
      )}
    </ModalBase>
  );
}

// ── Aba Consultas ───────────────────────────────────────────────────────────

const FONTES = [
  { chave: 'cache', rotulo: 'Desde que o servidor ligou', ajuda: 'ERP e portais · acumulado do cache de planos' },
  { chave: '7', rotulo: 'Últimos 7 dias', ajuda: 'só os nossos portais (o histórico por dia do ERP começa com a coleta de 15 min)', soPortais: true },
  { chave: '30', rotulo: 'Últimos 30 dias', ajuda: 'só os nossos portais (o histórico por dia do ERP começa com a coleta de 15 min)', soPortais: true },
];

export default function Consultas({ origemInicial, onAbrir, cpuServidor }) {
  const [fonte, setFonte] = useState('cache');
  const [origem, setOrigem] = useState(origemInicial || '');
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => { setOrigem(origemInicial || ''); }, [origemInicial]);
  const f = FONTES.find((x) => x.chave === fonte);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const params = fonte === 'cache'
        ? { fonte: 'cache', origem: origem || undefined, busca: buscaAplicada || undefined, limite: 300 }
        : { fonte: 'periodo', dias: Number(fonte), origem: origem || undefined, busca: buscaAplicada || undefined, limite: 300 };
      const { data } = await axios.get(`${API}/analise-banco/consultas`, { params });
      setDados(data.data);
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível carregar as consultas.');
    } finally {
      setCarregando(false);
    }
  }, [fonte, origem, buscaAplicada]);
  useEffect(() => { carregar(); }, [carregar]);

  const linhas = dados?.linhas || [];
  // % sempre sobre o SERVIDOR inteiro (mesmo numero do Resumo). No periodo
  // (so portais) nao ha total do servidor por dia: aí o % e sobre o que aparece.
  const total = fonte === 'cache' ? (dados?.cpuTotal || cpuServidor) : linhas.reduce((s, l) => s + Number(l.cpu_ms), 0);
  const { ordenadas, Th } = useOrdenacao(linhas, null);

  const exportar = () => exportToExcel(
    `consultas-pesadas-${new Date().toISOString().slice(0, 10)}`,
    [
      { label: 'Origem', value: (l) => (ORIGEM[l.origem] || ORIGEM.outro).rotulo },
      { label: 'Consulta', value: (l) => l.nome },
      { label: 'Sinais', value: (l) => (l.sinais || []).join(', ') },
      { label: 'Execuções', value: (l) => Number(l.execucoes) },
      { label: 'CPU (h)', value: (l) => dec(Number(l.cpu_ms) / 3600000, 1) },
      { label: '% da CPU do servidor', value: (l) => pct(l.cpu_ms, total) },
      { label: 'Normal', value: (l) => duracao(media(l.duracao_ms, l.execucoes)) },
      { label: 'Pior', value: (l) => duracao(l.max_duracao_ms) },
      { label: 'Leitura por execução', value: (l) => porExecucao(l.leituras, l.execucoes) },
      { label: 'Comando', value: (l) => l.trecho },
    ],
    ordenadas,
    `Consultas pesadas — ${f.rotulo}`,
  );

  return (
    <>
      <div className="filter-card" style={{ marginBottom: 14 }}>
        <div className="filter-card__field">
          <label>Período</label>
          <div className="portal-form-actions" style={{ margin: 0 }}>
            {FONTES.map((x) => (
              <button key={x.chave} type="button" onClick={() => { setFonte(x.chave); if (x.soPortais) setOrigem(''); }}
                className={`btn-portal ${fonte === x.chave ? 'btn-portal--primary' : 'btn-portal--outline'}`}>{x.rotulo}</button>
            ))}
          </div>
        </div>
        {!f.soPortais && (
          <div className="filter-card__field">
            <label>Origem</label>
            <div className="ab-chips">
              <button type="button" className={`vw-chip ${!origem ? 'vw-chip--on' : 'vw-chip--off'}`} onClick={() => setOrigem('')}>Todas</button>
              {ORDEM_ORIGENS.map((o) => (
                <button key={o} type="button" className={`vw-chip ${origem === o ? 'vw-chip--on' : 'vw-chip--off'}`}
                  style={origem === o ? { '--cor': ORIGEM[o].cor } : undefined} onClick={() => setOrigem(o)}>
                  <span className="vw-chip__cor" style={{ background: ORIGEM[o].cor }} />{ORIGEM[o].rotulo}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="filter-card__field filter-card__field--grow">
          <label>Buscar no comando ou no objeto</label>
          <form onSubmit={(e) => { e.preventDefault(); setBuscaAplicada(busca.trim()); }} className="d-flex gap-2">
            <input className="form-control" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="ex.: PEDICLICAD, ELMAH, fio_mensagem" />
            <button type="submit" className="btn-portal btn-portal--outline" aria-label="Buscar"><i className="bi bi-search" /></button>
          </form>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ marginBottom: 8 }}>
        <span className="cell-muted">
          {f.ajuda}{fonte === 'cache' && dados?.coleta ? ` · copiado em ${dataHora(dados.coleta.iniciada_em)}` : ''}
          {!carregando && ` · ${num(linhas.length)} consultas`}
        </span>
        <button type="button" className="btn-portal btn-portal--outline" onClick={exportar} disabled={!linhas.length}>
          <i className="bi bi-file-earmark-excel" /> Excel
        </button>
      </div>

      {erro && <div className="feedback-banner feedback-banner--error">{erro}</div>}
      {carregando ? <Carregando texto="Carregando consultas..." /> : (
        <div className="table-shell">
          <table className="portal-table">
            <thead>
              <tr>
                <Th campo="origem">Origem</Th>
                <Th campo="nome">Consulta</Th>
                <th>Sinais</th>
                <Th campo="execucoes" direita>Execuções</Th>
                <Th campo="cpu_ms" direita titulo="Percentual da CPU do servidor inteiro">% da CPU</Th>
                <Th campo="media" valor={(l) => media(l.duracao_ms, l.execucoes)} direita>Normal</Th>
                <Th campo="max_duracao_ms" direita>Pior</Th>
                <Th campo="leitura_exec" valor={(l) => Number(l.leituras) / Math.max(1, Number(l.execucoes))} direita titulo="Média lida na memória a cada execução">Leitura por execução</Th>
              </tr>
            </thead>
            <tbody>
              {ordenadas.map((l) => (
                <tr key={l.id} onClick={() => onAbrir(l.id)} style={{ cursor: 'pointer' }}>
                  <td><PillOrigem origem={l.origem} /></td>
                  <td className="ab-consulta">
                    <span className="cell-strong">{l.nome}</span>
                    <code className="ab-trecho">{l.trecho}</code>
                  </td>
                  <td>{(l.sinais || []).map((s) => <span key={s} className="ab-sinal">{s}</span>)}</td>
                  <td style={{ textAlign: 'right' }}>{num(l.execucoes)}</td>
                  <td style={{ textAlign: 'right' }}><span className="cell-strong">{pct(l.cpu_ms, total)}</span><br /><small className="cell-muted">{horasCpu(l.cpu_ms)}</small></td>
                  <td style={{ textAlign: 'right' }}>{duracao(media(l.duracao_ms, l.execucoes))}</td>
                  <td style={{ textAlign: 'right' }}>{duracao(l.max_duracao_ms)}</td>
                  <td style={{ textAlign: 'right' }}>{porExecucao(l.leituras, l.execucoes)}</td>
                </tr>
              ))}
              {!ordenadas.length && <tr><td colSpan={8} className="cell-muted">Nenhuma consulta com esse filtro.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
